import { Link, useParams } from 'react-router-dom';

import { ARCHIVE, UPCOMING, VENUES } from '@/data/events';
import { useI18n } from '@/providers/i18n';
import type { ArchiveEvent, Lineup, MoctEvent } from '@/types';

import EventDetailHeader from './components/EventDetailHeader';
import EventDossier from './components/EventDossier';
import EventPoster from './components/EventPoster';
import EventTicketRail from './components/EventTicketRail';
import LineupStages from './components/LineupStages';
import styles from './EventDetailPage.module.css';

type DetailEvent = {
  id: string;
  code: string;
  title: string;
  subtitle?: string;
  date: string;
  dateEnd?: string;
  doors?: string;
  close?: string;
  venue: MoctEvent['venue'];
  state?: MoctEvent['state'];
  flagship?: boolean;
  lineup: Lineup;
  posterUrl?: string;
};

function fromUpcoming(e: MoctEvent): DetailEvent {
  return {
    id: e.id,
    code: e.code,
    title: e.title,
    subtitle: e.subtitle,
    date: e.date,
    dateEnd: e.dateEnd,
    doors: e.doors,
    close: e.close,
    venue: e.venue,
    state: e.state,
    flagship: e.flagship,
    lineup: e.lineup,
    posterUrl: e.posterUrl,
  };
}

function fromArchive(e: ArchiveEvent): DetailEvent {
  return {
    id: e.id,
    code: e.code,
    title: e.title,
    date: e.date,
    venue: e.venue,
    lineup: { HEADLINERS: e.headliners },
    posterUrl: e.posterUrl,
  };
}

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();

  const upcoming = UPCOMING.find((e) => e.id === id) ?? null;
  const archived = upcoming ? null : ARCHIVE.find((e) => e.id === id) ?? null;
  const source = upcoming ?? archived;
  const isArchive = !!archived;

  if (!source) {
    return (
      <section className={styles.notFound}>
        <h1 className={`upper tight ${styles.notFoundTitle}`}>404 / NO TRANSMISSION</h1>
        <p className="fg-dim">No event with id &ldquo;{id}&rdquo;.</p>
        <Link to="/events" className="btn btn-arrow" style={{ marginTop: 24 }}>
          {t('cta.back')}
        </Link>
      </section>
    );
  }

  const event = upcoming ? fromUpcoming(upcoming) : fromArchive(archived!);
  const venue = VENUES[event.venue];
  const eyebrow = isArchive ? 'ARCHIVE' : event.flagship ? 'FLAGSHIP' : 'CLUB NIGHT';

  return (
    <section
      className={styles.root}
      data-screen-label={isArchive ? 'archive detail' : 'event detail'}
    >
      <EventDetailHeader event={event} venue={venue} archived={isArchive} />

      <div className={styles.grid}>
        <aside className={styles.rail}>
          <EventPoster
            code={event.code}
            src={event.posterUrl}
            alt={`${event.title} poster`}
          />
          {!isArchive && upcoming ? <EventTicketRail event={upcoming} /> : null}
        </aside>

        <div className={styles.stream}>
          <header className={styles.title}>
            <div className={styles.titleH}>
              <span>/ EVENT · {event.code}</span>
              <span className={styles.dim}>{eyebrow}</span>
            </div>
            <h1>{event.title}</h1>
            {event.subtitle ? <p className={styles.sub}>{event.subtitle}</p> : null}
          </header>
          <EventDossier event={event} venue={venue} archived={isArchive} />
          <LineupStages lineup={event.lineup} />
        </div>
      </div>
    </section>
  );
}
